// Merging trees from two devices. Every person, link, group, source, media item, to-do, map
// location, and dismissed suggestion is merged on its own: each keeps its most recently edited copy, and a deletion wins
// over any copy that wasn't edited after it. The edit and deletion times live in tree.sync and are
// recorded by stampEdits() on every edit (ui/store.ts commit). Whole trees are matched by id; a
// deleted tree's id and time are kept per device (see planSync's deletedTrees).
//
// Both devices compute both directions from the same two sets of trees, so each shows the same
// preview, and the merge is symmetric: merging A into B gives the same tree as B into A.

import { displayName } from './people';
import { MAP_KINDS } from './places';
import { sourceLabel } from './research';
import { SUGGESTION_RULES, suggestions, type SuggestionKind } from './suggestions';
import type { Settings, Tree, TreeSync } from './types';

export const COLLECTIONS = ['people', 'relationships', 'groups', 'sources', 'media', 'todos', 'locations', 'dismissed'] as const;
export type Collection = (typeof COLLECTIONS)[number];
/** The tree's own fields that sync, each as one item. */
const FIELDS = ['name', 'homePersonId', 'passthrough'] as const;
type Field = (typeof FIELDS)[number];

type Items = Record<string, unknown>;
const itemsOf = (t: Tree, c: Collection): Items => (t[c] ?? {}) as Items;
const json = (x: unknown) => JSON.stringify(x ?? null);
const emptySync = (): TreeSync => ({ edited: {}, deleted: {} });

/**
 * `next` with the edit and deletion times of everything that changed since `prev` set to `now`.
 * Returns `next` itself when nothing synced changed (or it's a different tree).
 */
export function stampEdits(prev: Tree, next: Tree, now: string): Tree {
  if (prev === next || prev.id !== next.id) return next;
  // From the tree being replaced: undo brings back an older tree, whose own times would forget
  // the edits and deletions since.
  const base = prev.sync ?? next.sync ?? emptySync();
  let edited: Record<string, string> | null = null;
  let deleted: Record<string, string> | null = null;
  const edit = (key: string) => ((edited ??= { ...base.edited })[key] = now);
  const remove = (key: string) => {
    (deleted ??= { ...base.deleted })[key] = now;
    if ((edited ??= { ...base.edited })[key]) delete edited[key];
  };
  for (const c of COLLECTIONS) {
    const a = itemsOf(prev, c);
    const b = itemsOf(next, c);
    if (a === b) continue;
    for (const [id, item] of Object.entries(b)) {
      const old = a[id];
      // Edits replace changed items, so an item kept by reference is unchanged; a copy with the
      // same contents isn't an edit either.
      if (old !== item && (old === undefined || json(old) !== json(item))) edit(`${c}:${id}`);
    }
    for (const id of Object.keys(a)) if (!(id in b)) remove(`${c}:${id}`);
  }
  for (const f of FIELDS) if (prev[f] !== next[f] && json(prev[f]) !== json(next[f])) edit(`tree:${f}`);
  if (!edited && !deleted) return !prev.sync || next.sync === prev.sync ? next : { ...next, sync: prev.sync };
  // Undo brings back the older tree's updatedAt too, but it's an edit now: a sync must weigh it
  // against a deletion of the whole tree, and a recovery copy against the saved one.
  const updatedAt = next.updatedAt > now ? next.updatedAt : now;
  return { ...next, updatedAt, sync: { edited: edited ?? base.edited, deleted: deleted ?? base.deleted } };
}

/** The later of each pair of times. */
function latest(a: Record<string, string> = {}, b: Record<string, string> = {}): Record<string, string> {
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) if (!out[k] || v > out[k]) out[k] = v;
  return out;
}

/**
 * One tree from its copies on two devices. Copies with the same edit time (both from before edits
 * were timed, say) settle on the same one on both devices.
 */
export function mergeTrees(local: Tree, remote: Tree): Tree {
  const le = local.sync?.edited ?? {};
  const re = remote.sync?.edited ?? {};
  const deleted = latest(local.sync?.deleted, remote.sync?.deleted);
  const edited: Record<string, string> = {};
  const pick = (key: string, l: unknown, r: unknown, hasL: boolean, hasR: boolean): { value: unknown; at: string } => {
    const tl = le[key] ?? '';
    const tr = re[key] ?? '';
    if (hasL && hasR) return tl > tr || (tl === tr && json(l) >= json(r)) ? { value: l, at: tl } : { value: r, at: tr };
    return hasL ? { value: l, at: tl } : { value: r, at: tr };
  };
  const out: Tree = {
    ...local,
    createdAt: local.createdAt < remote.createdAt ? local.createdAt : remote.createdAt,
    updatedAt: local.updatedAt > remote.updatedAt ? local.updatedAt : remote.updatedAt,
  };
  for (const c of COLLECTIONS) {
    const lc = itemsOf(local, c);
    const rc = itemsOf(remote, c);
    const merged: Items = {};
    for (const id of [...new Set([...Object.keys(lc), ...Object.keys(rc)])].sort()) {
      const key = `${c}:${id}`;
      const { value, at } = pick(key, lc[id], rc[id], id in lc, id in rc);
      if (deleted[key] !== undefined && deleted[key] >= at) continue;
      merged[id] = value;
      if (at) edited[key] = at;
    }
    if ((c === 'locations' || c === 'dismissed') && !local[c] && !remote[c]) delete out[c];
    else (out as unknown as Record<string, Items>)[c] = merged;
  }
  for (const f of FIELDS) {
    const key = `tree:${f}`;
    // A field that was cleared (the home person, undone) is an edit too, so both copies are weighed.
    const { value, at } = pick(key, local[f], remote[f], true, true);
    if (value === undefined) delete out[f];
    else (out as unknown as Record<Field, unknown>)[f] = value;
    if (at) edited[key] = at;
  }
  out.sync = { edited, deleted };
  return out;
}

export interface TreesPlan {
  /** Trees to write here: new ones and merged copies that differ from this device's. */
  put: Tree[];
  /** This device's trees that were deleted on the other device. */
  del: Tree[];
  /** Deleted trees' ids and times, from both devices. */
  deletedTrees: Record<string, string>;
}

/** What a sync changes on the device holding `local`. */
export function planSync(local: Tree[], localDeleted: Record<string, string>, remote: Tree[], remoteDeleted: Record<string, string>): TreesPlan {
  const deletedTrees = latest(localDeleted, remoteDeleted);
  const dead = (id: string, updatedAt: string) => deletedTrees[id] !== undefined && deletedTrees[id] >= updatedAt;
  const mine = new Map(local.map((t) => [t.id, t]));
  const theirs = new Map(remote.map((t) => [t.id, t]));
  const put: Tree[] = [];
  const del: Tree[] = [];
  for (const r of remote) {
    const l = mine.get(r.id);
    if (!l) {
      if (!dead(r.id, r.updatedAt)) put.push(r);
      continue;
    }
    if (dead(r.id, l.updatedAt > r.updatedAt ? l.updatedAt : r.updatedAt)) {
      del.push(l);
      continue;
    }
    const m = mergeTrees(l, r);
    if (!sameTree(l, m)) put.push(m);
  }
  for (const l of local) if (!theirs.has(l.id) && dead(l.id, l.updatedAt)) del.push(l);
  return { put, del, deletedTrees };
}

function sameTree(a: Tree, b: Tree): boolean {
  if (a.updatedAt !== b.updatedAt || json(a.sync) !== json(b.sync)) return false;
  return !itemChanges(a, b).length;
}

// ---------------------------------------------------------------- describing a sync

export type ItemType = Collection | 'tree';

export interface ItemChange {
  kind: 'added' | 'updated' | 'removed';
  type: ItemType;
  label: string;
  /** For updates: readable names of the fields that differ. */
  fields?: string[];
}

export interface TreeChange {
  id: string;
  name: string;
  kind: 'added' | 'removed' | 'changed';
  /** For a new tree, how many people it has. */
  people?: number;
  items: ItemChange[];
}

export interface SidePreview {
  trees: TreeChange[];
  /** Device-wide choices that change, one line each (see describeSettings). */
  settings: string[];
  empty: boolean;
}

export const TYPE_LABELS: Record<ItemType, string> = {
  people: 'People',
  relationships: 'Links',
  groups: 'Groups',
  sources: 'Sources',
  media: 'Media',
  todos: 'To-dos',
  locations: 'Places on the map',
  dismissed: 'Dismissed suggestions',
  tree: 'Tree',
};

const FIELD_LABELS: Record<string, string> = {
  names: 'names',
  events: 'life events',
  living: 'living or deceased',
  terms: 'relationship words',
  citations: 'sources',
  passthrough: 'other GEDCOM details',
  a: 'people',
  b: 'people',
  members: 'members',
  sourceIds: 'sources',
  file: 'file',
  thumb: 'picture',
  photo: 'photo',
  lat: 'position',
  lon: 'position',
  due: 'due date',
  doneAt: 'done',
};

const titles = new WeakMap<Tree, Map<string, string>>();

/** What a suggestion says, in the tree it ends up in (it may no longer apply there). */
function suggestionTitle(t: Tree, id: string): string {
  let byId = titles.get(t);
  if (!byId) titles.set(t, (byId = new Map(suggestions(t).map((x) => [x.id, x.title]))));
  return byId.get(id) ?? 'A suggestion that no longer applies';
}

/** One line naming an item, using the tree it ends up in for names of the people it links. */
function itemLabel(type: Collection, item: unknown, id: string, t: Tree): string {
  const person = (pid: string) => displayName(t.people[pid]) || 'someone';
  switch (type) {
    case 'people':
      return displayName(item as Tree['people'][string]) || 'Unnamed person';
    case 'relationships': {
      const r = item as Tree['relationships'][string];
      const what = r.kind === 'parent' ? 'parent of' : r.kind === 'partner' ? 'partners with' : r.kind === 'sibling' ? 'siblings with' : r.type === 'mentor' ? 'mentor of' : 'friends with';
      return `${person(r.a)} ${what} ${person(r.b)}`;
    }
    case 'groups':
      return (item as Tree['groups'][string]).name || 'Unnamed group';
    case 'sources':
      return sourceLabel(item as Tree['sources'][string]);
    case 'media':
      return (item as Tree['media'][string]).title || 'Untitled media';
    case 'todos':
      return (item as Tree['todos'][string]).title || 'Untitled to-do';
    case 'locations':
      return `Where “${id}” is`;
    case 'dismissed':
      return suggestionTitle(t, id);
  }
}

/** Readable names of the top-level fields that differ between two copies of an item. */
export function changedFields(before: unknown, after: unknown): string[] {
  const a = (before ?? {}) as Record<string, unknown>;
  const b = (after ?? {}) as Record<string, unknown>;
  const out: string[] = [];
  for (const k of new Set([...Object.keys(a), ...Object.keys(b)])) {
    if (k === 'id' || json(a[k]) === json(b[k])) continue;
    const label = FIELD_LABELS[k] ?? k.replace(/([A-Z])/g, ' $1').toLowerCase();
    if (!out.includes(label)) out.push(label);
  }
  return out;
}

/** Everything that differs between two copies of a tree, as seen by the device holding `before`. */
export function itemChanges(before: Tree, after: Tree): ItemChange[] {
  const out: ItemChange[] = [];
  for (const c of COLLECTIONS) {
    const a = itemsOf(before, c);
    const b = itemsOf(after, c);
    if (a === b) continue;
    for (const [id, item] of Object.entries(b)) {
      if (!(id in a)) out.push({ kind: 'added', type: c, label: itemLabel(c, item, id, after) });
      else if (a[id] !== item && json(a[id]) !== json(item)) out.push({ kind: 'updated', type: c, label: itemLabel(c, item, id, after), fields: changedFields(a[id], item) });
    }
    for (const [id, item] of Object.entries(a)) if (!(id in b)) out.push({ kind: 'removed', type: c, label: itemLabel(c, item, id, before) });
  }
  if (before.name !== after.name) out.push({ kind: 'updated', type: 'tree', label: `Renamed to “${after.name}”` });
  if (before.homePersonId !== after.homePersonId && after.homePersonId) out.push({ kind: 'updated', type: 'tree', label: `Home person set to ${displayName(after.people[after.homePersonId]) || 'someone'}` });
  if (json(before.passthrough) !== json(after.passthrough)) out.push({ kind: 'updated', type: 'tree', label: 'Other GEDCOM records' });
  return out;
}

/** What a plan from planSync changes on the device holding `local`, with any lines from describeSettings. */
export function describePlan(plan: TreesPlan, local: Tree[], settings: string[] = []): SidePreview {
  const mine = new Map(local.map((t) => [t.id, t]));
  const trees: TreeChange[] = [];
  for (const t of plan.put) {
    const cur = mine.get(t.id);
    if (!cur) trees.push({ id: t.id, name: t.name, kind: 'added', people: Object.keys(t.people).length, items: [] });
    else {
      const items = itemChanges(cur, t);
      if (items.length) trees.push({ id: t.id, name: t.name, kind: 'changed', items });
    }
  }
  for (const t of plan.del) trees.push({ id: t.id, name: t.name, kind: 'removed', items: [] });
  return { trees, settings, empty: !trees.length && !settings.length };
}

// ---------------------------------------------------------------- settings

// Settings sync too, except the camera option (whether a network needs it depends on the device).
// Each single setting keeps its most recently changed value, and each list (kinds of suggestion
// turned off, kinds of place hidden on the map) merges item by item, like the items in a tree.
// Times live in settings.settingsAt: `theme` for a single setting, `mapHiddenKinds:birth` for an
// item in a list.
//
// What was set before changes were timed can't be told apart from untouched defaults (every save
// stored the defaults too), so an untimed setting never overrides anything: each device keeps its
// own until one is changed again. A list item that's there untimed was added on purpose, though,
// so it still beats a device that never had it (but not a later removal).

export const SYNCED_VALUES = ['theme', 'themeId', 'placeSearch', 'mapStyle'] as const;
export const SYNCED_LISTS = ['hiddenSuggestionKinds', 'mapHiddenKinds'] as const;
type SyncedValue = (typeof SYNCED_VALUES)[number];
type SyncedList = (typeof SYNCED_LISTS)[number];

export type SyncedSettings = Partial<Pick<Settings, SyncedValue | SyncedList | 'settingsAt'>>;

/** The settings that sync, with times from older versions (suggestionKindsAt) moved into settingsAt. */
export function syncedSettings(s: Partial<Settings>): SyncedSettings {
  const out: SyncedSettings = {};
  for (const k of [...SYNCED_VALUES, ...SYNCED_LISTS]) if (s[k] !== undefined) (out as Record<string, unknown>)[k] = s[k];
  const legacy = Object.fromEntries(Object.entries(s.suggestionKindsAt ?? {}).map(([k, t]) => [`hiddenSuggestionKinds:${k}`, t]));
  out.settingsAt = { ...legacy, ...s.settingsAt };
  return out;
}

/** The settings after a change to `next` from `prev`, with each changed setting and list item stamped `now`. */
export function stampSettings(prev: Settings, next: Settings, now: string): Settings {
  const changed: string[] = [];
  for (const k of SYNCED_VALUES) if (json(prev[k]) !== json(next[k])) changed.push(k);
  for (const k of SYNCED_LISTS) {
    const before = new Set(prev[k] ?? []);
    const after = new Set(next[k] ?? []);
    for (const x of new Set([...before, ...after])) if (before.has(x) !== after.has(x)) changed.push(`${k}:${x}`);
  }
  if (!changed.length) return next;
  return { ...next, settingsAt: { ...next.settingsAt, ...Object.fromEntries(changed.map((k) => [k, now])) } };
}

/**
 * This device's synced settings merged with another's. Everything timed comes out the same on both
 * devices; untimed single settings stay as each device has them.
 */
export function mergeSettings(local: SyncedSettings, remote: SyncedSettings): SyncedSettings {
  const la = local.settingsAt ?? {};
  const ra = remote.settingsAt ?? {};
  const out: SyncedSettings = { settingsAt: latest(la, ra) };
  const o = out as Record<string, unknown>;
  for (const k of SYNCED_VALUES) {
    const tl = la[k] ?? '';
    const tr = ra[k] ?? '';
    const value = tr > tl ? remote[k] : local[k];
    if (value !== undefined) o[k] = value;
  }
  for (const k of SYNCED_LISTS) {
    const lh = new Set<string>(local[k] ?? []);
    const rh = new Set<string>(remote[k] ?? []);
    const items: string[] = [];
    // Local order first, so a sync that changes nothing doesn't reorder the list.
    for (const x of new Set([...lh, ...rh, ...keysOf(la, k), ...keysOf(ra, k)])) {
      const tl = la[`${k}:${x}`] ?? '';
      const tr = ra[`${k}:${x}`] ?? '';
      if (tl > tr ? lh.has(x) : tr > tl ? rh.has(x) : lh.has(x) || rh.has(x)) items.push(x);
    }
    if (items.length || local[k] || remote[k]) o[k] = items;
  }
  return out;
}

const keysOf = (at: Record<string, string>, list: string) =>
  Object.keys(at)
    .filter((key) => key.startsWith(`${list}:`))
    .map((key) => key.slice(list.length + 1));

const MAP_KIND_LABELS: Record<string, string> = Object.fromEntries(MAP_KINDS.map((m) => [m.kind, m.label]));

/** What a merge changes in the device's settings, one line each. themeName names a color theme by id. */
export function describeSettings(before: SyncedSettings, after: SyncedSettings, themeName: (id: string) => string = (id) => id): string[] {
  const out: string[] = [];
  const differs = (k: SyncedValue) => after[k] !== undefined && json(before[k]) !== json(after[k]);
  if (differs('theme')) out.push(after.theme === 'system' ? 'Light or dark: follows the system' : `Light or dark: always ${after.theme}`);
  if (differs('themeId')) out.push(`Color theme set to ${themeName(after.themeId!)}`);
  if (differs('placeSearch') && (before.placeSearch ?? true) !== after.placeSearch) out.push(after.placeSearch ? 'OpenStreetMap turned on' : 'OpenStreetMap turned off');
  if (differs('mapStyle') && (before.mapStyle ?? 'street') !== after.mapStyle) out.push(after.mapStyle === 'outline' ? 'Map shows the outline' : 'Map shows the street map');
  const list = (k: SyncedList, label: (x: string) => string, off: string, on: string) => {
    const was = new Set(before[k] ?? []);
    const now = new Set(after[k] ?? []);
    for (const x of now) if (!was.has(x)) out.push(`${label(x)} ${off}`);
    for (const x of was) if (!now.has(x)) out.push(`${label(x)} ${on}`);
  };
  list('hiddenSuggestionKinds', (k) => `“${SUGGESTION_RULES[k as SuggestionKind]?.label ?? k}” suggestions`, 'turned off', 'turned back on');
  list('mapHiddenKinds', (k) => `“${MAP_KIND_LABELS[k] ?? k}” places`, 'hidden on the map', 'shown on the map again');
  return out;
}

/** Keys of the media files the trees use. */
export function filesUsed(trees: Tree[]): Set<string> {
  const keys = new Set<string>();
  for (const t of trees) for (const m of Object.values(t.media ?? {})) if (m.file) keys.add(m.file.key);
  return keys;
}
